import { HeaderResultOption } from '../../apps/professional-app/fa/MeetingSummary/MeetingResults/ResultCard/Header/HeaderResultOptions/HeaderResultOptionsButton'
import { useChangePrivacyMutation, useChangeSourceMutation } from '../../store/media/media'
import { useCallback, useMemo, useState } from 'react'
import { RoomSession } from '../../models/Room'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { parseMetadata } from '../../common/util'
import { EndRoomSessionModal } from '../../apps/professional-app/fa/EndRoomSessionModal'
import { InPersonMeetingFileProvider } from '../../apps/in-person-meeting/InPersonMeetingProvider'
import { useZocksAuth, useZocksQuota } from '../../components/ScopedBlock/hooks'
import { Stack, Toast, ToastContainer } from 'react-bootstrap'
import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'
import { IntegrationServiceId, PHONE_RECORDING_CAPABILITY, supportedCallSolutions } from '../../common/integrations'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { getRoomTrackingDetails } from '../../apps/professional-app/fa/tracking'
import { LoggerProvider } from '../../components/Logger/LoggerProvider'
import { useLoggerConfig } from '../../apps/in-person-meeting/hooks/useLoggerConfig'
import { useGetMeQuery } from '../../store/user/user'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { LinkGotoModal, LinkPhoneNumberModal, LinkRingCentralModal } from './LinkPhoneNumberModal'
import { ReactComponent as ZocksIcon } from '../../assets/ZocksIcon.svg'
import { usePrivateMeetingsEnabled } from '../../common/usePrivateMeetingsEnabled'
import { GotoIconComponent, RingCentralIconComponent, ZoomPhoneIconComponent } from './AdditionalOptionsIcons'

export const useChangeToInPersonOption = (
  roomSession: RoomSession,
  onSessionChange?: (roomSession: RoomSession) => void,
): { resultOption: HeaderResultOption | null; isLoading: boolean } => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const [changeSource, { isLoading }] = useChangeSourceMutation()
  const { roomId, id: sessionId } = roomSession
  const { hasScope } = useZocksAuth()
  const hasUpdateSessionScope = hasScope('**', 'room_session:update', ['room_session:admin', 'room:admin'])

  const _handleChangeToInPerson = async () => {
    await changeSource({ accountId, roomId, sessionId, source: 'voice-memo' })
    onSessionChange?.({ ...roomSession, metadata: JSON.stringify({ ...parseMetadata(roomSession.metadata), source: 'voice-memo' }) })
  }

  const handleChangeToInPerson = tracked(_handleChangeToInPerson, {
    eventName: TrackEvent.CHANGED_TO_IN_PERSON,
    eventDetails: getRoomTrackingDetails(roomSession),
  })

  const { source } = parseMetadata(roomSession.metadata)
  if (!source || ['import', 'voice-memo'].includes(source) || (roomSession.finishedAt && roomSession.finishedAt < new Date().toISOString())) {
    return { resultOption: null, isLoading }
  }

  const disabled = roomSession.isActive || isLoading || !hasUpdateSessionScope

  return {
    resultOption: {
      selectOption: {
        id: 'changeToInPerson',
        label: 'Change to In-Person Meeting',
        disabled,
      },
      icon: 'bi-laptop',
      handleSelected: () => {
        if (!disabled) {
          handleChangeToInPerson()
        }
      },
    },
    isLoading,
  }
}

export const useChangeToPhoneOption = (
  roomSession: RoomSession,
  onSessionChange?: (roomSession: RoomSession) => void,
): { resultOption: HeaderResultOption | null; isLoading: boolean } => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [changeSource, { isLoading }] = useChangeSourceMutation()
  const [showModal, setShowModal] = useState<boolean>(false)
  const { roomId, id: sessionId } = roomSession
  const { hasScope } = useZocksAuth()
  const hasUpdateSessionScope = hasScope('**', 'room_session:update', ['room_session:admin', 'room:admin'])
  const hasPhoneIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.PHONE && i.state === 'live'))
  }, [userData])
  const phoneConfig = useMemo(() => {
    if (!account?.data?.effectiveConfig?.integrations) {
      return null
    }
    return (
      (account.data.effectiveConfig.integrations.find(
        (integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone',
      ) as MeetingProviderAccountIntegrationConfig) ?? null
    )
  }, [account])
  const canUsePhone = hasPhoneIntegration || (phoneConfig?.sharedPhoneLineEnabled ?? false)

  const _applyChangeToPhone = async () => {
    await changeSource({ accountId, roomId, sessionId, source: 'phone' })
    onSessionChange?.({ ...roomSession, metadata: JSON.stringify({ ...parseMetadata(roomSession.metadata), source: 'phone' }) })
  }

  const applyChangeToPhone = tracked(_applyChangeToPhone, {
    eventName: TrackEvent.CHANGED_TO_PHONE,
    eventDetails: getRoomTrackingDetails(roomSession),
  })

  const handleChangeToPhone = () => {
    if (!canUsePhone) {
      setShowModal(true)
      return
    }
    applyChangeToPhone()
  }
  const { source, provider = null } = parseMetadata(roomSession.metadata)

  if ((['import', 'phone'].includes(source) && provider === null) || (roomSession.finishedAt && roomSession.finishedAt < new Date().toISOString())) {
    return { resultOption: null, isLoading }
  }

  const disabled = roomSession.isActive || !roomSession.finishedAt || isLoading || !hasUpdateSessionScope

  return {
    resultOption: {
      selectOption: {
        id: 'changeToPhone',
        label: 'Change to Phone Meeting',
        disabled,
      },
      icon: 'bi-telephone',
      handleSelected: () => {
        if (!disabled) {
          handleChangeToPhone()
        }
      },
      children: showModal ? <LinkPhoneNumberModal show={showModal} onHide={() => setShowModal(false)} /> : null,
    },
    isLoading,
  }
}

export const useChangeToRingCentralOption = (
  roomSession: RoomSession,
  onSessionChange?: (roomSession: RoomSession) => void,
): { resultOption: HeaderResultOption | null; isLoading: boolean } => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [changeSource, { isLoading }] = useChangeSourceMutation()
  const [showModal, setShowModal] = useState<boolean>(false)
  const { roomId, id: sessionId } = roomSession
  const { hasScope } = useZocksAuth()
  const hasUpdateSessionScope = hasScope('**', 'room_session:update', ['room_session:admin', 'room:admin'])
  const hasRingCentralIntegration = !!(
    userData &&
    userData.integrations &&
    userData.integrations.some((i) => i.serviceId === IntegrationServiceId.RINGCENTRAL && i.state === 'live')
  )

  const _applyChangeToRingCentral = async () => {
    await changeSource({ accountId, roomId, sessionId, source: IntegrationServiceId.PHONE, provider: IntegrationServiceId.RINGCENTRAL })
    onSessionChange?.({
      ...roomSession,
      metadata: JSON.stringify({ ...parseMetadata(roomSession.metadata), source: IntegrationServiceId.PHONE, provider: IntegrationServiceId.RINGCENTRAL }),
    })
  }

  const applyChangeToRingCentral = tracked(_applyChangeToRingCentral, {
    eventName: TrackEvent.CHANGED_TO_RINGCENTRAL,
    eventDetails: getRoomTrackingDetails(roomSession),
  })

  const handleChangeToRingCentral = () => {
    if (!hasRingCentralIntegration) {
      setShowModal(true)
      return
    }
    applyChangeToRingCentral()
  }

  if (!hasRingCentralIntegration) {
    return { resultOption: null, isLoading }
  }

  const { source, provider = null } = parseMetadata(roomSession.metadata)
  if (
    (['import', 'phone'].includes(source) && provider === IntegrationServiceId.RINGCENTRAL) ||
    (roomSession.finishedAt && roomSession.finishedAt < new Date().toISOString())
  ) {
    return { resultOption: null, isLoading }
  }

  const disabled = roomSession.isActive || !roomSession.finishedAt || isLoading || !hasUpdateSessionScope

  return {
    resultOption: {
      selectOption: {
        id: 'changeToRingCentral',
        label: 'Change to RingCentral Meeting',
        disabled,
      },
      IconComponent: RingCentralIconComponent,
      handleSelected: () => {
        if (!disabled) {
          handleChangeToRingCentral()
        }
      },
      children: showModal ? <LinkRingCentralModal show={showModal} onHide={() => setShowModal(false)} /> : null,
    },
    isLoading,
  }
}

export const useChangeToGotoOption = (
  roomSession: RoomSession,
  onSessionChange?: (roomSession: RoomSession) => void,
): { resultOption: HeaderResultOption | null; isLoading: boolean } => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [changeSource, { isLoading }] = useChangeSourceMutation()
  const [showModal, setShowModal] = useState<boolean>(false)
  const { roomId, id: sessionId } = roomSession
  const { hasScope } = useZocksAuth()
  const hasUpdateSessionScope = hasScope('**', 'room_session:update', ['room_session:admin', 'room:admin'])
  const hasGotoIntegration = !!(
    userData &&
    userData.integrations &&
    userData.integrations.some((i) => i.serviceId === IntegrationServiceId.GOTO && i.state === 'live')
  )

  const _applyChangeToGoto = async () => {
    await changeSource({ accountId, roomId, sessionId, source: IntegrationServiceId.PHONE, provider: IntegrationServiceId.GOTO })
    onSessionChange?.({
      ...roomSession,
      metadata: JSON.stringify({ ...parseMetadata(roomSession.metadata), source: IntegrationServiceId.PHONE, provider: IntegrationServiceId.GOTO }),
    })
  }

  const applyChangeToGoto = tracked(_applyChangeToGoto, {
    eventName: TrackEvent.CHANGED_TO_GOTO,
    eventDetails: getRoomTrackingDetails(roomSession),
  })

  const handleChangeToGoto = () => {
    if (!hasGotoIntegration) {
      setShowModal(true)
      return
    }
    applyChangeToGoto()
  }

  if (!hasGotoIntegration) {
    return { resultOption: null, isLoading }
  }

  const { source, provider = null } = parseMetadata(roomSession.metadata)
  if (
    (['import', 'phone'].includes(source) && provider === IntegrationServiceId.GOTO) ||
    (roomSession.finishedAt && roomSession.finishedAt < new Date().toISOString())
  ) {
    return { resultOption: null, isLoading }
  }

  const disabled = roomSession.isActive || !roomSession.finishedAt || isLoading || !hasUpdateSessionScope

  return {
    resultOption: {
      selectOption: {
        id: 'changeToGoto',
        label: 'Change to GoTo Meeting',
        disabled,
      },
      IconComponent: GotoIconComponent,
      handleSelected: () => {
        if (!disabled) {
          handleChangeToGoto()
        }
      },
      children: showModal ? <LinkGotoModal onHide={() => setShowModal(false)} /> : null,
    },
    isLoading,
  }
}

export const useChangeToZoomPhoneOption = (
  roomSession: RoomSession,
  onSessionChange?: (roomSession: RoomSession) => void,
): { resultOption: HeaderResultOption | null; isLoading: boolean } => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [changeSource, { isLoading }] = useChangeSourceMutation()
  const { roomId, id: sessionId } = roomSession
  const { hasScope } = useZocksAuth()
  const { hasQuota } = useZocksQuota()
  const hasUpdateSessionScope = hasScope('**', 'room_session:update', ['room_session:admin', 'room:admin'])
  const zoomPhoneFeatureEnabled = hasQuota('integration.zoomPhoneEnabled.value')
  const hasZoomPhone = !!(
    userData &&
    userData.integrations &&
    userData.integrations.some(
      (i) =>
        i.serviceId === IntegrationServiceId.ZOOM &&
        i.state === 'live' &&
        (i.capabilities?.includes(PHONE_RECORDING_CAPABILITY) ?? false) &&
        (i.phoneConfiguration?.enabled ?? false),
    )
  )

  const _applyChangeToZoomPhone = async () => {
    await changeSource({ accountId, roomId, sessionId, source: IntegrationServiceId.PHONE, provider: 'zoom_phone' })
    onSessionChange?.({
      ...roomSession,
      metadata: JSON.stringify({ ...parseMetadata(roomSession.metadata), source: IntegrationServiceId.PHONE, provider: 'zoom_phone' }),
    })
  }

  const applyChangeToZoomPhone = tracked(_applyChangeToZoomPhone, {
    eventName: TrackEvent.CHANGED_TO_ZOOM_PHONE,
    eventDetails: getRoomTrackingDetails(roomSession),
  })

  if (!zoomPhoneFeatureEnabled || !hasZoomPhone) {
    return { resultOption: null, isLoading }
  }

  const { source, provider = null } = parseMetadata(roomSession.metadata)
  if ((['import', 'phone'].includes(source) && provider === 'zoom_phone') || (roomSession.finishedAt && roomSession.finishedAt < new Date().toISOString())) {
    return { resultOption: null, isLoading }
  }

  const disabled = roomSession.isActive || !roomSession.finishedAt || isLoading || !hasUpdateSessionScope

  return {
    resultOption: {
      selectOption: {
        id: 'changeToZoomPhone',
        label: 'Change to Zoom Phone Meeting',
        disabled,
      },
      IconComponent: ZoomPhoneIconComponent,
      handleSelected: () => {
        if (!disabled) {
          applyChangeToZoomPhone()
        }
      },
      children: null,
    },
    isLoading,
  }
}

export const useEndMeetingOption = (roomSession: RoomSession): HeaderResultOption | null => {
  const accountId = useAppSelector(selectAccountId)
  const [showModal, setShowModal] = useState<boolean>(false)
  const { hasScope } = useZocksAuth()
  const hasFinishSessionScope = hasScope('**', 'room_session:finish', ['room_session:admin', 'room:admin'])
  const [aiAssistantRemoved, setAIAssistantRemoved] = useState<boolean>(false)
  const [showToast, setShowToast] = useState(false)
  const { source, external: { platform = null, userAgent = null } = {} } = parseMetadata(roomSession?.metadata || '{}')
  const startedFromMobileApp = source === 'voice-memo' && !platform && userAgent

  const handleShowEndMeetingModal = useCallback(() => setShowModal(true), [])

  const handleHideEndMeetingModal = useCallback(() => setShowModal(false), [])

  const handleSessionEnded = useCallback(() => {
    setAIAssistantRemoved(true)
    setShowModal(false)
    setShowToast(true)
  }, [])

  const { zocksAssistantStatus } = parseMetadata(roomSession.metadata)
  const { loggerConfig } = useLoggerConfig(accountId, roomSession)

  if (!roomSession.isActive || !['inWaitingRoom', 'analyzing'].includes(zocksAssistantStatus)) {
    return null
  }

  const disabled = !hasFinishSessionScope || aiAssistantRemoved || startedFromMobileApp

  return {
    selectOption: {
      id: 'endMeeting',
      label: 'Remove AI Assistant',
      disabled,
    },
    handleSelected: () => {
      if (!disabled) {
        handleShowEndMeetingModal()
      }
    },
    IconComponent: () => <ZocksIcon width='1rem' height='1rem' fill='var(--zocks-gray-900)' />,
    children: (
      <>
        <ToastContainer position='top-center' className='p-3 summary-toast'>
          <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide>
            <Toast.Body>Removing AI Assistant... This may take a few seconds.</Toast.Body>
          </Toast>
        </ToastContainer>
        {showModal ? (
          <LoggerProvider config={loggerConfig}>
            <InPersonMeetingFileProvider>
              <EndRoomSessionModal roomSession={roomSession} onSessionEnded={handleSessionEnded} show={showModal} onHide={handleHideEndMeetingModal} />
            </InPersonMeetingFileProvider>
          </LoggerProvider>
        ) : null}
      </>
    ),
  }
}

export const useAddToOngoingCallOption = (
  roomSession: RoomSession,
  phoneConfig: MeetingProviderAccountIntegrationConfig | null,
  onSessionSelectedForCall?: (details: { session: RoomSession; doCall: boolean }) => void,
): HeaderResultOption | null => {
  const { hasScope } = useZocksAuth()
  const { source } = parseMetadata(roomSession.metadata)
  const provider = useMemo(() => {
    return supportedCallSolutions.find((integration) => integration.id === phoneConfig?.defaultProvider) ?? null
  }, [phoneConfig])
  const hasStartSessionScope = hasScope('**', 'room_session:start', ['room_session:admin', 'room:admin'])

  if (roomSession.isActive || source !== 'phone' || !phoneConfig?.sharedPhoneLineEnabled || !provider?.id || provider.id === 'intulse') {
    return null
  }

  const disabled = !hasStartSessionScope || roomSession.isActive

  return {
    selectOption: {
      id: 'addToOngoingCall',
      label: 'Add to ongoing call',
      disabled,
    },
    icon: 'bi-telephone',
    handleSelected: () => {
      if (!disabled && onSessionSelectedForCall) {
        onSessionSelectedForCall({ session: roomSession, doCall: false })
      }
    },
  }
}

export const useChangePrivacyOption = (
  roomSession: RoomSession,
  onSessionChange?: (roomSession: RoomSession) => void,
): { resultOption: HeaderResultOption | null; isLoading: boolean } => {
  const { tracked } = useTracking()
  const accountId = useAppSelector(selectAccountId)
  const [changePrivacy, { isLoading }] = useChangePrivacyMutation()
  const { roomId, id: sessionId, isPrivate } = roomSession
  const { hasScope } = useZocksAuth()
  const hasUpdateSessionScope = hasScope('**', 'room_session:update', ['room_session:admin', 'room:admin'])
  const { privateMeetingsEnabled } = usePrivateMeetingsEnabled()

  const _handleChangePrivacy = async () => {
    await changePrivacy({ accountId, roomId, sessionId, isPrivate: !isPrivate }).unwrap()
    onSessionChange?.({ ...roomSession, isPrivate: !isPrivate })
  }

  const handleChangePrivacy = tracked(_handleChangePrivacy, {
    eventName: TrackEvent.CHANGED_PRIVACY,
    eventDetails: { ...getRoomTrackingDetails(roomSession), isPrivate },
  })

  if (!privateMeetingsEnabled) {
    return { resultOption: null, isLoading }
  }

  const disabled = isLoading || !hasUpdateSessionScope
  const displayTitle = `Change to ${isPrivate ? 'public' : 'private'}`
  const displayDescription = isPrivate ? 'Visible to everyone' : 'Visible only to participants'
  return {
    resultOption: {
      selectOption: {
        id: 'changePrivacy',
        label: displayTitle,
        displayLabel: (
          <Stack>
            <p className='paragraph-small mb-0'>{displayTitle}</p>
            <p className='paragraph-extra-small mb-0'>{displayDescription}</p>
          </Stack>
        ),
        disabled,
      },
      icon: isPrivate ? 'bi-unlock' : 'bi-lock',
      handleSelected: () => {
        if (!disabled) {
          handleChangePrivacy()
        }
      },
    },
    isLoading,
  }
}

export const useGroupHeaderOption = (id: string, label: string): HeaderResultOption => {
  return {
    selectOption: {
      id: `group_${id}`,
      label,
    },
    className: 'group-header',
    handleSelected: () => {},
  }
}

export const useGroupDividerOption = (id: string): HeaderResultOption => {
  return {
    selectOption: {
      id: `divider_${id}`,
      label: '',
    },
    className: 'group-divider',
    handleSelected: () => {},
  }
}
